import React from 'react';

import {
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { IconAlertCircle, IconEllipseShade } from '@/components';

import { useInterviewQuestionContext } from './InterviewQuestion.context';
import {
  interviewQueryKeys,
  useEarlyFinishQuestionMutation,
} from './useInterviewQueries';

export const FinishEarlyButton = () => {
  const { t } = useTranslation(['interview', 'common']);
  const { securityToken, candidateId, question } =
    useInterviewQuestionContext();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = React.useState(false);

  const earlyFinishMutation = useEarlyFinishQuestionMutation(securityToken, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: interviewQueryKeys.getAllCandidatesQuestions(),
      });
      setIsOpen(false);
    },
  });

  const handleConfirm = () => {
    if (!question?.id) return;
    earlyFinishMutation.mutate({
      candidateId,
      questionId: question?.id,
    });
  };

  return (
    <>
      <Button
        flex={{ base: '1', md: 'auto' }}
        onClick={() => setIsOpen(true)}
        variant="outline"
        size={{ base: 'sm', lg: 'md' }}
        ml="auto"
      >
        {t('interview:finishInterview')}
      </Button>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" maxW="420px" p={{ base: 4, md: 8 }}>
          <ModalCloseButton boxSize={5} top={3} right={3} />
          <ModalBody p={0} pt={8}>
            <VStack spacing={6} align="center">
              <Stack px={10}>
                <Flex justifyContent="center" position="relative" mt={3}>
                  <IconEllipseShade
                    fontSize={64}
                    position="absolute"
                    top={-6}
                  />
                  <Flex
                    h={12}
                    w={12}
                    borderWidth="1px"
                    borderColor="error.500"
                    borderRadius="50%"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <IconAlertCircle color="error.500" fontSize={28} />
                  </Flex>
                </Flex>
              </Stack>
              <VStack spacing={1} align="center">
                <Text
                  fontWeight="semibold"
                  fontSize="20px"
                  color="#2F3236"
                  textAlign="center"
                >
                  {t('interview:finishInterviewConfirmation')}
                </Text>
                <Text color="#656D75" fontSize="16px" textAlign="center">
                  {t('interview:finishInterviewConfirmationDescription')}
                </Text>
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter pt={8} px={0}>
            <HStack w="full" spacing={3}>
              <Button
                onClick={() => setIsOpen(false)}
                flex={1}
                variant="outline"
                size="lg"
                rounded="lg"
              >
                {t('common:actions.cancel2')}
              </Button>
              <Button
                onClick={handleConfirm}
                flex={1}
                rounded="lg"
                colorScheme="purple"
                size="lg"
                variant="@primary"
                isLoading={earlyFinishMutation.isPending}
              >
                {t('common:actions.yes') + ', ' + t('common:actions.finish')}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
